import type { QuizConfig, QuizQuestion } from './quiz-config';

export type QuizAnswers = Record<string, string[]>;
export type QuizAnswerResult = { answers: QuizAnswers; error?: string };

export function validateQuizConfig(config: QuizConfig): boolean {
  if (!config.quizVersion || !['demo', 'formal_preview', 'formal'].includes(config.reviewStatus)
    || !Array.isArray(config.questions) || config.questions.length === 0) return false;
  const seen = new Set<string>();
  for (const question of config.questions) {
    if (!/^[a-z0-9_]{1,64}$/.test(question.id) || seen.has(question.id) || !question.prompt || !question.help) return false;
    if (!['single', 'multi', 'image'].includes(question.kind) || !Number.isInteger(question.maxSelections) || question.maxSelections < 1) return false;
    if ((question.kind === 'single' || question.kind === 'image') && question.maxSelections !== 1) return false;
    const optionCodes = new Set<string>();
    for (const option of question.options) {
      if (!/^[a-z0-9_-]{1,64}$/.test(option.code) || optionCodes.has(option.code) || !option.label) return false;
      optionCodes.add(option.code);
    }
    if (question.visibleWhen) {
      const parent = config.questions.find((candidate) => candidate.id === question.visibleWhen!.questionId);
      if (!parent || !seen.has(parent.id) || question.visibleWhen.optionCodes.length === 0) return false;
      const parentCodes = new Set(parent.options.map((option) => option.code));
      if (!question.visibleWhen.optionCodes.every((code) => parentCodes.has(code))) return false;
    }
    seen.add(question.id);
  }
  return true;
}

export function getVisibleQuestions(config: QuizConfig, answers: QuizAnswers): QuizQuestion[] {
  return config.questions.filter((question) => {
    if (!question.visibleWhen) return true;
    const parentAnswers = answers[question.visibleWhen.questionId] ?? [];
    return question.visibleWhen.optionCodes.some((code) => parentAnswers.includes(code));
  });
}

export function sanitizeQuizAnswers(config: QuizConfig, answers: QuizAnswers): QuizAnswers {
  const sanitized: QuizAnswers = {};
  for (const question of config.questions) {
    const visible = !question.visibleWhen || question.visibleWhen.optionCodes.some(
      (code) => (sanitized[question.visibleWhen!.questionId] ?? []).includes(code),
    );
    if (!visible || !Array.isArray(answers[question.id])) continue;
    const allowed = new Set(question.options.map((option) => option.code));
    const codes = [...new Set(answers[question.id])].filter((code) => allowed.has(code));
    if (codes.length > 0 && codes.length <= question.maxSelections) sanitized[question.id] = codes;
  }
  return sanitized;
}

export function setQuizAnswer(config: QuizConfig, answers: QuizAnswers, questionId: string, codes: string[]): QuizAnswerResult {
  if (!validateQuizConfig(config)) return { answers, error: 'The question configuration is invalid.' };
  const question = getVisibleQuestions(config, answers).find((candidate) => candidate.id === questionId);
  if (!question) return { answers, error: 'That question is not available in this branch.' };
  if (!Array.isArray(codes) || codes.length < 1 || codes.length > question.maxSelections || new Set(codes).size !== codes.length) {
    return { answers, error: `Choose no more than ${question.maxSelections} option${question.maxSelections === 1 ? '' : 's'}.` };
  }
  const allowed = new Set(question.options.map((option) => option.code));
  if (!codes.every((code) => allowed.has(code))) return { answers, error: 'That option is not part of this question.' };
  return { answers: sanitizeQuizAnswers(config, { ...answers, [questionId]: codes }) };
}

export function skipQuizQuestion(config: QuizConfig, answers: QuizAnswers, questionId: string): QuizAnswerResult {
  const question = getVisibleQuestions(config, answers).find((candidate) => candidate.id === questionId);
  if (!question) return { answers, error: 'That question is not available in this branch.' };
  if (question.required) return { answers, error: 'Choose one option to continue.' };
  const next = { ...answers };
  delete next[questionId];
  return { answers: sanitizeQuizAnswers(config, next) };
}

export function quizProgress(config: QuizConfig, answers: QuizAnswers, currentQuestionId: string): { step: number; total: number; completedPercent: number } {
  const visible = getVisibleQuestions(config, answers);
  const index = Math.max(0, visible.findIndex((question) => question.id === currentQuestionId));
  return { step: index + 1, total: visible.length, completedPercent: visible.length === 0 ? 0 : Math.round((index / visible.length) * 100) };
}
